import { useCallback, useMemo, useRef, useState } from 'react';
import type { OrderStatus } from '@/types';
import { useCartStore } from '@store/cartStore';
import type { OrderCardOrder } from '@molecules/OrderCard';
import { orderService } from '@services/orderService';

type ToastType = 'success' | 'error' | 'info' | 'warning';

export function useSmartReorder(params: {
  onToast: (type: ToastType, message: string) => void;
  onAfterSuccess?: () => void;
}) {
  const { onToast, onAfterSuccess } = params;
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingOrderId, setProcessingOrderId] = useState<string | null>(null);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const pendingOrderRef = useRef<OrderCardOrder | null>(null);

  const hasCartConflict = useCallback((restaurantId: string) => {
    const state = useCartStore.getState();
    const hasItems = state.items.length > 0;
    const boundRestaurantId = state.restaurantId;
    return Boolean(hasItems && boundRestaurantId && boundRestaurantId !== restaurantId);
  }, []);

  const executeReorder = useCallback(
    async (order: OrderCardOrder, options: { clearFirst: boolean }) => {
      setIsProcessing(true);
      setProcessingOrderId(order.id);
      try {
        const cartState = useCartStore.getState();
        if (!options.clearFirst && hasCartConflict(order.restaurantId)) {
          pendingOrderRef.current = order;
          setIsConfirmOpen(true);
          return;
        }

        await orderService.reorderOrder({ orderId: order.id, replaceCart: options.clearFirst });
        await cartState.syncFromBackend();

        onToast('success', 'Ürünler sepete eklendi.');
        onAfterSuccess?.();
      } catch (err: any) {
        const message =
          err.response?.data?.error?.message ||
          err.message ||
          'Tekrar sipariş oluşturulurken bir hata oluştu.';
        onToast('error', message);
      } finally {
        setIsProcessing(false);
        setProcessingOrderId(null);
      }
    },
    [hasCartConflict, onAfterSuccess, onToast],
  );

  const startReorder = useCallback(
    async (order: OrderCardOrder) => {
      await executeReorder(order, { clearFirst: false });
    },
    [executeReorder],
  );

  const confirmReplaceCart = useCallback(async () => {
    const pending = pendingOrderRef.current;
    if (!pending) {
      setIsConfirmOpen(false);
      return;
    }
    setIsConfirmOpen(false);
    pendingOrderRef.current = null;
    await executeReorder(pending, { clearFirst: true });
  }, [executeReorder]);

  const closeConfirm = useCallback(() => {
    setIsConfirmOpen(false);
    pendingOrderRef.current = null;
  }, []);

  const confirmModal = useMemo(() => {
    return {
      isOpen: isConfirmOpen,
      title: 'Sepet temizlensin mi?',
      description:
        'Sepetinizde başka bir restorana ait ürünler var. Sepeti temizleyip bu siparişi eklemek ister misiniz?',
      confirmText: 'Sepeti Temizle ve Ekle',
      cancelText: 'Vazgeç',
      isConfirmLoading: isProcessing,
      onConfirm: confirmReplaceCart,
      onClose: closeConfirm,
    };
  }, [closeConfirm, confirmReplaceCart, isConfirmOpen, isProcessing]);

  return { startReorder, confirmModal, isProcessing, processingOrderId };
}

